import { Component } from 'react'
import muxjs from 'mux.js'
import shaka from 'shaka-player'
import { canUseDOM } from '../../../../../utils/canUseDOM.js'

// Shaka Player MPEG-2 TS: With mux.js v6.2.0+, can be played on any browser which supports MP4
canUseDOM && (window.muxjs = muxjs)

export default class ShakaPlayer extends Component {
  ref = (videoElement) => {
    this.videoElement = videoElement
  }

  getVideoElement() {
    return this.videoElement
  }

  async componentDidMount() {
    const { drm } = this.props

    shaka.polyfill.installAll()
    drm && shaka.polyfill.PatchedMediaKeysApple.install()

    // Create a Player instance.
    const video = this.videoElement
    this.addListeners(video)

    const player = (this.player = new shaka.Player(video))
    player.configure({
      streaming: {
        preferNativeHls: this.props.nativeHLS ?? false,
        bufferingGoal: 90,
      },
      drm: drm && {
        servers: {
          'com.widevine.alpha': drm.licenseServers.widevine,
          'com.apple.fps': drm.licenseServers.fairplay,
          'com.apple.fps.1_0': drm.licenseServers.fairplay,
          // PlayReady doesn't work well with CBCS encryption used to support HLS for FairPlay
          // Edge works fine with Widevine, so we remove the PlayReady config entirely to force that
          // 'com.microsoft.playready': drm.licenseServers.playready,
        },
        advanced: {
          'com.widevine.alpha': {
            videoRobustness: 'SW_SECURE_CRYPTO', // Seems to be the maximum supported by Chrome 111
            audioRobustness: 'SW_SECURE_CRYPTO',
          },
          'com.apple.fps': {
            serverCertificateUri: drm.certificateUrls.fairplay,
          },
          'com.apple.fps.1_0': {
            serverCertificateUri: drm.certificateUrls.fairplay,
          },
          // 'com.microsoft.playready': {
          //   videoRobustness: '2000', // https://learn.microsoft.com/en-us/playready/overview/security-level
          //   audioRobustness: '2000',
          // },
        },
        initDataTransform: (initData, initDataType, context) => {
          // Return without changes for DASH
          if (initDataType !== 'skd') return initData
          // HLS: 'initData' is a buffer containing an 'skd://' URL as a UTF-8 string.
          const skdUri = shaka.util.StringUtils.fromBytesAutoDetect(initData)
          // Axinom DRM requires that everything after the "skd://" (scheme) part is extracted.
          const contentId = skdUri.replace(/^.*:\/\//, '')
          const cert = player.drmInfo().serverCertificate
          return shaka.util.FairPlayUtils.initDataTransform(initData, contentId, cert)
        },
      },
    })
    if (drm) {
      player.getNetworkingEngine().registerRequestFilter(function (type, request) {
        if (type === shaka.net.NetworkingEngine.RequestType.LICENSE) {
          request.headers['X-AxDRM-Message'] = drm.licenseToken
        }
      })
    }
    player.addEventListener('error', (event) => {
      // Extract the shaka.util.Error object from the event.
      this.onError(event.detail)
    })

    await this.load()
    this.props.onMount?.(this)
  }

  componentDidUpdate(prevProps) {
    if (this.props.url !== prevProps.url) {
      this.load()
    }
  }

  componentWillUnmount() {
    this.player.destroy()
    this.removeListeners(this.videoElement)
  }

  addListeners(videoElement) {
    videoElement.addEventListener('play', this.onPlay)
    videoElement.addEventListener('waiting', this.onBuffer)
    videoElement.addEventListener('playing', this.onBufferEnd)
    videoElement.addEventListener('pause', this.onPause)
    videoElement.addEventListener('seeked', this.onSeek)
    videoElement.addEventListener('ended', this.onEnded)
    videoElement.addEventListener('error', this.onError)
    videoElement.addEventListener('canplay', this.onReady)
  }

  removeListeners(videoElement) {
    videoElement.removeEventListener('play', this.onPlay)
    videoElement.removeEventListener('waiting', this.onBuffer)
    videoElement.removeEventListener('playing', this.onBufferEnd)
    videoElement.removeEventListener('pause', this.onPause)
    videoElement.removeEventListener('seeked', this.onSeek)
    videoElement.removeEventListener('ended', this.onEnded)
    videoElement.removeEventListener('error', this.onError)
    videoElement.removeEventListener('canplay', this.onReady)
  }

  // Proxy methods to prevent listener leaks
  onReady = (...args) => this.props.onReady(...args)
  onPlay = (...args) => this.props.onPlay(...args)
  onBuffer = (...args) => this.props.onBuffer(...args)
  onBufferEnd = (...args) => this.props.onBufferEnd(...args)
  onPause = (...args) => this.props.onPause(...args)
  onEnded = (...args) => this.props.onEnded(...args)
  onError = (...args) => this.props.onError(...args)

  onSeek = (e) => {
    this.props.onSeek(e.target.currentTime)
  }

  async load(url = this.props.url, mime = this.props.mime) {
    if (this.player && url) {
      return this.player.load(url, null, mime).catch(this.onError)
    }
  }

  play() {
    // Older browsers don't return a promise
    const promise = this.videoElement.play()
    if (promise) {
      promise.then(this.props.onPlaySuccess).catch(this.props.onError)
    } else {
      this.props.onPlaySuccess()
    }
    return promise ?? Promise.resolve()
  }

  // Play content right from the live edge
  playLiveEdge() {
    this.player.goToLive()
  }

  pause() {
    this.videoElement.pause()
  }

  seekTo(seconds) {
    this.videoElement.currentTime = seconds
  }

  setVolume(fraction) {
    this.videoElement.volume = fraction
  }

  mute = () => {
    this.videoElement.muted = true
  }

  unmute = () => {
    this.videoElement.muted = false
  }

  setPlaybackRate(rate) {
    this.videoElement.playbackRate = rate
  }

  getDuration() {
    if (!this.videoElement) return null
    const { duration, seekable } = this.videoElement
    // on iOS, live streams return Infinity for the duration,
    // so instead we use the end of the seekable time range
    if (duration === Infinity && seekable.length > 0) {
      return seekable.end(seekable.length - 1)
    }
    return duration
  }

  getCurrentTime() {
    if (!this.videoElement) return null
    return this.videoElement.currentTime
  }

  getSecondsLoaded() {
    if (!this.videoElement) return null
    const { buffered } = this.videoElement
    if (buffered.length === 0) {
      return 0
    }
    const end = buffered.end(buffered.length - 1)
    const duration = this.getDuration()
    if (end > duration) {
      return duration
    }
    return end
  }

  render() {
    const { playing, muted, width, height, playsInline, controls } = this.props
    return (
      <video
        ref={this.ref}
        style={{ width, height }}
        playsInline={playsInline}
        autoPlay={playing || undefined}
        muted={muted}
        controls={controls}
      />
    )
  }
}
